import { DownloadIcon } from "@components/Icons/Download";
import { encodePin } from "@helpers/codec";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import html2canvas from "html2canvas";
import { FC, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useBranding } from "src/helpers/useBranding";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useToast } from "src/helpers/useToast";

type Props = {
  challenge: Challenge;
};

/**
 * QR-code deeplink generator for a challenge (shown as a tab in the Edit modal). The event PIN is
 * embedded in the link (`?p=`), and optionally a team to auto-join (`&teamId=`) — e.g. a
 * pre-created "Anonymous" team so visitors scanning location QR codes land straight on the
 * challenge page. Only passwordless teams are offered (the backend rejects auto-joining
 * password-protected ones).
 */
export const ChallengeLink: FC<Props> = ({ challenge }) => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const [event] = useEvent();
  const { appDomain } = useBranding();
  const toast = useToast();
  const { getTranslatedValue } = useTranslatedKey();

  const [teams] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const joinableTeams = useMemo(() => teams?.filter((team) => !team.isUploader && !team.password), [teams]);
  const [teamId, setTeamId] = useState("");

  const componentRef = useRef<HTMLDivElement>(null);
  const [qrDownloading, setQrDownloading] = useState(false);

  if (!event) return null;

  const title = getTranslatedValue(challenge, "title");
  const pinEncoded = encodePin(event.pin);
  const url =
    `https://${appDomain}/${eventId}/challenges/${challenge.id}?p=${pinEncoded}` + (teamId ? `&teamId=${teamId}` : "");

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(t("linkCopied"), "🔗", "success");
    } catch (error) {
      console.error(error);
    }
  };

  const exportAsImage = async () => {
    try {
      const canvas = await html2canvas(componentRef.current);
      const image = canvas.toDataURL("image/png", 1.0);

      const fakeLink = window.document.createElement("a");

      const safeTitle = (title ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      const fileName = `${eventId}-${safeTitle || challenge.id}.png`;
      fakeLink.download = fileName;
      fakeLink.href = image;
      document.body.appendChild(fakeLink);
      fakeLink.click();
      document.body.removeChild(fakeLink);
      fakeLink.remove();
    } catch (error) {
      console.error(error);
    } finally {
      setQrDownloading(false);
    }
  };

  const onDownload = () => {
    window.scrollTo(0, 0);
    setQrDownloading(true);
    setTimeout(() => {
      exportAsImage();
    }, 50);
  };

  return (
    <div>
      <p className="py-2 text-sm">{t("challengeLinkAbout")}</p>

      <div className="form-control mt-2 w-full">
        <label className="label">
          <span className="label-text">{t("challengeLinkTeam")}</span>
        </label>
        <select className="select-bordered select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">{t("challengeLinkNoTeam")}</option>
          {joinableTeams?.map((team) => (
            <option key={team.id} value={team.id}>
              {team.displayName ?? team.name}
            </option>
          ))}
        </select>
        <label className="label">
          <span className="label-text-alt text-neutral">{t("challengeLinkTeamHint")}</span>
        </label>
      </div>

      <div className="mt-4 flex flex-col items-center">
        <div
          ref={componentRef}
          className={`bg-white ${qrDownloading ? "px-16 py-8" : "p-4"}`}
          style={{ width: qrDownloading ? 1000 : "auto" }}
        >
          {qrDownloading && <h1 className="mb-12 text-center text-7xl font-bold text-black">{title}</h1>}
          <QRCode
            style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            size={qrDownloading ? 2048 : 256}
            viewBox={`0 0 ${qrDownloading ? 2048 : 256} ${qrDownloading ? 2048 : 256}`}
            value={url}
          />
        </div>
        <p className="mt-4 w-full break-all rounded-lg bg-base-200 p-3 text-xs">{url}</p>
        <div className="mt-4 flex gap-3">
          <button type="button" className="btn-primary btn" onClick={onCopy}>
            {t("copyLink")}
          </button>
          <button type="button" className="btn-primary btn" onClick={onDownload} disabled={qrDownloading}>
            <DownloadIcon className="mr-2 h-5 w-5" />
            {t("download")}
          </button>
        </div>
      </div>
    </div>
  );
};
